import {register, CloudObject, defineProperty} from 'olympe';
import {checkLabelValueObject} from '../../utils/checkObjects';
import {accountTypeOptions} from '../../components/_helpers';

/**
 * @typedef {{label: string, value: string}} LabelValue
 */

/**
 * @typedef{{label: string, value: string, email: string}} User
 */

/**
 * @typedef {{companyCode: LabelValue, accountType: LabelValue, plant: LabelValue, purchaseOrganization: LabelValue, storageLocation: LabelValue, deliveryAddress: DeliveryAddress, purchasingGroup: LabelValue, glAccount: LabelValue, taxCode: LabelValue, wbsProject: LabelValue, costCenter: LabelValue, profitCenter: LabelValue, currency: LabelValue, spender: User, requester: User, hidePrice: boolean}} LabelValueObject
 */

/**
 * @typedef {{company_code: string=, account_type: string=, plant: string=, purchase_organization: string=, storage_location: string=, delivery_address: string=, purchasing_group: string=, gl_number: string=, tax_code: string=, wbs_project: string=, cost_center: string=, profit_center: string=, currency: string=, spender: string=, requester: string=, hide_price: boolean=}} PGObject
 */

/**
 * @extends {CloudObject}
 */
export default class UserDefaults extends CloudObject {
    /**
     * Creates an instance of UserDefaults.
     *
     * @param {!Transaction} transaction
     * @return {!string} the tag of the created instance
     */
    static create(transaction) {
        return transaction.create(UserDefaults);
    }

    /**
     * @param {{id: string, description: string, email: string}} user
     * @return {{companyCode: {label: string, value: string}, requester: {}, spender: {}, costCenter: {label: string, value: string}, accountType: {label: string, value: string}, purchaseOrganization: {label: string, value: string}, storageLocation: {label: string, value: string}, purchasingGroup: {label: string, value: string}, hidePrice: boolean, profitCenter: {label: string, value: string}, taxCode: {label: string, value: string}, deliveryAddress: DeliveryAddress, plant: {label: string, value: string}, wbsProject: {label: string, value: string}, currency: {label: string, value: string}, glAccount: {label: string, value: string}}}
     */
    getLabelValueObject(user) {
        /**
         *
         * @type {DeliveryAddress}
         */
        let address = {};
        let spender = {};
        let requester = {};
        try {
            address = JSON.parse(this.getDeliveryAddress()) || {};
            spender = JSON.parse(this.getSpender()) || {};
            requester = JSON.parse(this.getRequester()) || {};

            spender = JSON.stringify(spender) === '{}' ? {label: user.description, value: user.id, email: user.email} : spender;
        } catch (e) {
            // do nothing
        }
        return {
            storageLocation: checkLabelValueObject(this.getStorageLocation()),
            deliveryAddress: address,
            taxCode: checkLabelValueObject(this.getTaxCode()),
            glAccount: checkLabelValueObject(this.getGLAccount()),
            purchaseOrganization: checkLabelValueObject(this.getPurchOrg()),
            accountType: checkLabelValueObject(this.getAccountType()),
            purchasingGroup: checkLabelValueObject(this.getPurchGroup()),
            companyCode: checkLabelValueObject(this.getCompCode()),
            plant: checkLabelValueObject(this.getPlant()),
            wbsProject: checkLabelValueObject(this.getWBSProject()),
            costCenter: checkLabelValueObject(this.getCostCenter()),
            profitCenter: checkLabelValueObject(this.getProfitCenter()),
            currency: checkLabelValueObject(this.getCurrency()),
            spender: spender,
            requester: requester,
            hidePrice: this.getHidePrice(),
        };
    }

    /**
     *
     * @param {PGObject} pgData
     * @return {LabelValueObject}
     */
    static getLabelValueObjectFromPGData(pgData) {
        /**
         *
         * @type {DeliveryAddress}
         */
        let address = {};
        let spender = {};
        let requester = {};
        try {
            address = JSON.parse(pgData.delivery_address) || {};
            spender = JSON.parse(pgData.spender) || {};
            requester = JSON.parse(pgData.requester) || {};
        } catch (e) {
            // do nothing
        }
        return {
            storageLocation: checkLabelValueObject(pgData.storage_location),
            deliveryAddress: address,
            taxCode: checkLabelValueObject(pgData.tax_code),
            glAccount: checkLabelValueObject(pgData.gl_number),
            purchaseOrganization: checkLabelValueObject(pgData.purchase_organization),
            accountType: accountTypeOptions.find((option) => option.label === pgData.account_type),
            purchasingGroup: checkLabelValueObject(pgData.purchasing_group),
            companyCode: checkLabelValueObject(pgData.company_code),
            plant: checkLabelValueObject(pgData.plant),
            wbsProject: checkLabelValueObject(pgData.wbs_project),
            costCenter: checkLabelValueObject(pgData.cost_center),
            profitCenter: checkLabelValueObject(pgData.profit_center),
            currency: checkLabelValueObject(pgData.currency),
            spender: spender,
            requester: requester,
            hidePrice: pgData.hide_price,
        };
    }

    /**
     *
     * @param {Transaction} transaction
     * @param {LabelValueObject} obj
     * */
    setFromLabelValueObject(transaction, obj) {
        const props = new Map()
            .set(UserDefaults.storageLocationProp, obj.storageLocation.label)
            .set(UserDefaults.deliveryAddressProp, JSON.stringify(obj.deliveryAddress || {}))
            .set(UserDefaults.taxCodeProp, obj.taxCode.label)
            .set(UserDefaults.gLAccountProp, obj.glAccount.label)
            .set(UserDefaults.purchOrgProp, obj.purchaseOrganization.label)
            .set(UserDefaults.accountTypeProp, obj.accountType.label)
            .set(UserDefaults.purchGroupProp, obj.purchasingGroup.label)
            .set(UserDefaults.compCodeProp, obj.companyCode.label)
            .set(UserDefaults.plantProp, obj.plant.label)
            .set(UserDefaults.wBSProjectProp, obj.wbsProject.label)
            .set(UserDefaults.costCenterProp, obj.costCenter.label)
            .set(UserDefaults.profitCenterProp, obj.profitCenter.label)
            .set(UserDefaults.currencyProp, obj.currency.label)
            .set(UserDefaults.spenderProp, JSON.stringify(obj.spender || {}))
            .set(UserDefaults.requesterProp, JSON.stringify(obj.requester || {}))
            .set(UserDefaults.hidePriceProp, obj.hidePrice);
        transaction.multiUpdate(this, props);
    }

    /**
     * @return {string}
     */
    getStorageLocation() {
        return this.get(UserDefaults.storageLocationProp);
    }

    /**
     * @param {BrickContext} $
     * @return {!Observable<string>}
     */
    observeStorageLocation($) {
        return this.observe($, UserDefaults.storageLocationProp);
    }

    /**
     * @return {string}
     */
    getDeliveryAddress() {
        return this.get(UserDefaults.deliveryAddressProp);
    }

    /**
     * @param {BrickContext} $
     * @return {!Observable<string>}
     */
    observeDeliveryAddress($) {
        return this.observe($, UserDefaults.deliveryAddressProp);
    }

    /**
     * @return {string}
     */
    getTaxCode() {
        return this.get(UserDefaults.taxCodeProp);
    }

    /**
     * @param {BrickContext} $
     * @return {!Observable<string>}
     */
    observeTaxCode($) {
        return this.observe($, UserDefaults.taxCodeProp);
    }

    /**
     * @return {string}
     */
    getGLAccount() {
        return this.get(UserDefaults.gLAccountProp);
    }

    /**
     * @param {BrickContext} $
     * @return {!Observable<string>}
     */
    observeGLAccount($) {
        return this.observe($, UserDefaults.gLAccountProp);
    }

    /**
     * @return {string}
     */
    getPurchOrg() {
        return this.get(UserDefaults.purchOrgProp);
    }

    /**
     * @param {BrickContext} $
     * @return {!Observable<string>}
     */
    observePurchOrg($) {
        return this.observe($, UserDefaults.purchOrgProp);
    }

    /**
     * @return {string}
     */
    getAccountType() {
        return this.get(UserDefaults.accountTypeProp);
    }

    /**
     * @param {BrickContext} $
     * @return {!Observable<string>}
     */
    observeAccountType($) {
        return this.observe($, UserDefaults.accountTypeProp);
    }

    /**
     * @return {string}
     */
    getPurchGroup() {
        return this.get(UserDefaults.purchGroupProp);
    }

    /**
     * @param {BrickContext} $
     * @return {!Observable<string>}
     */
    observePurchGroup($) {
        return this.observe($, UserDefaults.purchGroupProp);
    }

    /**
     * @return {string}
     */
    getCompCode() {
        return this.get(UserDefaults.compCodeProp);
    }

    /**
     * @param {BrickContext} $
     * @return {!Observable<string>}
     */
    observeCompCode($) {
        return this.observe($, UserDefaults.compCodeProp);
    }

    /**
     * @return {string}
     */
    getAccountNumber() {
        return this.get(UserDefaults.accountNumberProp);
    }

    /**
     * @param {BrickContext} $
     * @return {!Observable<string>}
     */
    observeAccountNumber($) {
        return this.observe($, UserDefaults.accountNumberProp);
    }

    /**
     * @return {string}
     */
    getPlant() {
        return this.get(UserDefaults.plantProp);
    }

    /**
     * @param {BrickContext} $
     * @return {!Observable<string>}
     */
    observePlant($) {
        return this.observe($, UserDefaults.plantProp);
    }

    /**
     * @return {string}
     */
    getCurrency() {
        return this.get(UserDefaults.currencyProp);
    }

    /**
     * @param {BrickContext} $
     * @return {!Observable<string>}
     */
    observeCurrency($) {
        return this.observe($, UserDefaults.currencyProp);
    }

    /**
     * @return {string}
     */
    getProfitCenter() {
        return this.get(UserDefaults.profitCenterProp);
    }

    /**
     * @param {BrickContext} $
     * @return {!Observable<string>}
     */
    observeProfitCenter($) {
        return this.observe($, UserDefaults.profitCenterProp);
    }

    /**
     * @return {string}
     */
    getCostCenter() {
        return this.get(UserDefaults.costCenterProp);
    }

    /**
     * @param {BrickContext} $
     * @return {!Observable<string>}
     */
    observeCostCenter($) {
        return this.observe($, UserDefaults.costCenterProp);
    }

    /**
     * @return {string}
     */
    getWBSProject() {
        return this.get(UserDefaults.wBSProjectProp);
    }

    /**
     * @param {BrickContext} $
     * @return {!Observable<string>}
     */
    observeWBSProject($) {
        return this.observe($, UserDefaults.wBSProjectProp);
    }

    /**
     * @return {string}
     */
    getSpender() {
        return this.get(UserDefaults.spenderProp);
    }

    /**
     * @return {string}
     */
    getRequester() {
        return this.get(UserDefaults.requesterProp);
    }

    /**
     * @return {boolean}
     */
    getHidePrice() {
        return this.get(UserDefaults.hidePriceProp);
    }

    /**
     * @param {!BrickContext} $
     * @return {!Observable<boolean>}
     */
    observeHidePrice($) {
        return this.observe($, UserDefaults.hidePriceProp);
    }
}

// Hardcoded tags
register('017c7f22b2793c89b00c', UserDefaults);
UserDefaults.compCodeProp = defineProperty('017c7f22f7b6a86f1f35');
UserDefaults.purchOrgProp = defineProperty('017c7f22ffef7b28e2ac');
UserDefaults.purchGroupProp = defineProperty('017c7f23079957f279d0');
UserDefaults.plantProp = defineProperty('017c7f230f788ab4426e');
UserDefaults.gLAccountProp = defineProperty('017c7f26feef0f654bc5');
UserDefaults.accountTypeProp = defineProperty('017c7f2716ba3b67e37f');
UserDefaults.accountNumberProp = defineProperty('017c7f27717d6d457310');
UserDefaults.storageLocationProp = defineProperty('017f63d570aaecfeec40');
UserDefaults.taxCodeProp = defineProperty('017f63d57b63dd10ba9e');
UserDefaults.currencyProp = defineProperty('01804732722bbaa65963');
UserDefaults.wBSProjectProp = defineProperty('01804b66cf3b90441359');
UserDefaults.costCenterProp = defineProperty('01804b671ce6c0baffb9');
UserDefaults.profitCenterProp = defineProperty('01804b67447a201049f6');
UserDefaults.deliveryAddressProp = defineProperty('018113c823999d0cf8ff');
UserDefaults.spenderProp = defineProperty('018a9846cda8e181e85b');
UserDefaults.requesterProp = defineProperty('018a9846e0b581b5941e');
UserDefaults.hidePriceProp = defineProperty('018c3697d6f5445f58dd');
